import * as React from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StraightenIcon from "@mui/icons-material/Straighten";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import ScienceIcon from "@mui/icons-material/Science";
import { useNavigate } from "react-router-dom";
import { Talent, UserWorkStatus } from "../../types/users";
import { nl2br } from "../../utils/helper";
import { useGetTalentByIdQuery } from "../../services/talent.service";

import { useCompany } from "../../hooks/useCompany";
import { CompanyStatus } from "../../types/company";
import { GlassDrawer } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

interface TalentDrawerProps {
  data: Talent | null;
  forceRefresh?: boolean;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TalentDrawer(props: TalentDrawerProps) {
  const { data: dataProp, forceRefresh = false, open, setOpen } = props;
  const navigate = useNavigate();
  const [data, setData] = React.useState(dataProp);
  const industriesLabel = useUILabel("talent.industries");
  const noIndustryAddedLabel = useUILabel("talent.noIndustryAdded");
  const skillsLabel = useUILabel("talent.skills");
  const noSkillAddedLabel = useUILabel("talent.noSkillAdded");
  const languagesLabel = useUILabel("talent.languages");
  const noLanguagesAddedLabel = useUILabel("talent.noLanguagesAdded");
  const workStatusLabel = useUILabel("talent.workStatus");
  const availabilityLabel = useUILabel("talent.availability");
  const notSetLabel = useUILabel("talent.notSet");
  const soleProprietorLabel = useUILabel("talent.soleProprietor");
  const entrepreneurLabel = useUILabel("talent.entrepreneur");
  const partTimeLabel = useUILabel("talent.partTime");
  const moreInfoLabel = useUILabel("talent.moreInfo");
  const rateLabel = useUILabel("talent.rate");
  const commuteRadiusLabel = useUILabel("talent.commuteRadius");
  const allowBackgroundCheckLabel = useUILabel("talent.allowBackgroundCheck");
  const allowDrugTestLabel = useUILabel("talent.allowDrugTest");
  const moreDetailLabel = useUILabel("talent.moreDetail");
  const yesLabel = useUILabel("project.yes");
  const noLabel = useUILabel("project.no");

  const {
    data: dataApi,
    isError,
    isLoading,
  } = useGetTalentByIdQuery(dataProp?.id!, {
    skip: !forceRefresh || !dataProp,
    refetchOnMountOrArgChange: true,
  });

  React.useEffect(() => {
    if (!!dataApi && !isError) setData(dataApi);
    else setData(dataProp);
  }, [dataApi, dataProp, isError]);

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }
      setOpen(open);
    };
  const company = useCompany();

  if (!data) {
    return null;
  }

  const list = () => (
    <>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          justifyContent: "flex-end",
          mt: { xs: 0.5, sm: 1.5 },
        }}
      >
        <ClearIcon
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
          sx={{ cursor: "pointer" }}
        />
      </Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ px: { xs: 0.5, sm: 1 } }}
      >
        <Avatar
          alt={`${data?.first_name}`}
          src={`${data?.photo?.[0]?.original_url}`}
          sx={{ width: 100, height: 100 }}
        />
        <Box>
          <Typography variant="h6" sx={{ color: "#12903C" }}>
            {data?.first_name} {data?.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.city} {data?.state} {data?.zip}
          </Typography>
          <Rating value={data?.total_rating || 0} readOnly precision={0.5} />
        </Box>
      </Stack>
      <Box sx={{ px: { xs: 0.5, sm: 1 } }}>
        <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "text.primary" }} component="div">{nl2br(data?.bio)}</Typography>
      </Box>
      <Divider />

      <List
        sx={{
          "& .MuiListItem-root": {
            px: { xs: 0.5, sm: 1 },
          },
        }}
      >
        <ListItem disableGutters>
          <Stack spacing={1} sx={{ mt: 1, width: "100%" }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}>{industriesLabel}</Typography>
            <Box>
              {data?.business_verticals?.length == 0 ? (
                <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "text.secondary" }}>{noIndustryAddedLabel}</Typography>
              ) : (
                data?.business_verticals?.map((it) => (
                  <Chip
                    label={it.name}
                    key={it.id}
                    sx={{
                      mr: 1,
                      mb: 1,
                      backgroundColor: "rgba(18, 144, 60, 0.1)",
                      color: "#12903C",
                      fontWeight: 500,
                      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                      height: { xs: 28, sm: 32 },
                      borderRadius: 2,
                      border: "1px solid rgba(18, 144, 60, 0.2)",
                      "&:hover": {
                        backgroundColor: "rgba(18, 144, 60, 0.15)",
                        border: "1px solid rgba(18, 144, 60, 0.3)",
                      }
                    }}
                  />
                ))
              )}
            </Box>
          </Stack>
        </ListItem>
        <Divider />
        <ListItem disableGutters>
          <Stack spacing={1} sx={{ mt: 1, width: "100%" }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}>{skillsLabel}</Typography>
            <Box>
              {data?.skills?.length == 0 ? (
                <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "text.secondary" }}>{noSkillAddedLabel}</Typography>
              ) : (
                data?.skills?.map((it) => (
                  <Chip
                    label={it.name}
                    key={it.id}
                    sx={{
                      mr: 1,
                      mb: 1,
                      backgroundColor: "rgba(18, 144, 60, 0.1)",
                      color: "#12903C",
                      fontWeight: 500,
                      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                      height: { xs: 28, sm: 32 },
                      borderRadius: 2,
                      border: "1px solid rgba(18, 144, 60, 0.2)",
                      "&:hover": {
                        backgroundColor: "rgba(18, 144, 60, 0.15)",
                        border: "1px solid rgba(18, 144, 60, 0.3)",
                      }
                    }}
                  />
                ))
              )}
            </Box>
          </Stack>
        </ListItem>
        <Divider />
        <ListItem disableGutters>
          <Stack spacing={1} sx={{ mt: 1, width: "100%" }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}>{languagesLabel}</Typography>
            <Box>
              {data?.languages?.length == 0 ? (
                <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "text.secondary" }}>
                  {noLanguagesAddedLabel}
                </Typography>
              ) : (
                data?.languages?.map((it) => (
                  <Chip
                    label={it.name}
                    key={it.id}
                    sx={{
                      mr: 1,
                      mb: 1,
                      backgroundColor: "rgba(18, 144, 60, 0.1)",
                      color: "#12903C",
                      fontWeight: 500,
                      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                      height: { xs: 28, sm: 32 },
                      borderRadius: 2,
                      border: "1px solid rgba(18, 144, 60, 0.2)",
                      "&:hover": {
                        backgroundColor: "rgba(18, 144, 60, 0.15)",
                        border: "1px solid rgba(18, 144, 60, 0.3)",
                      }
                    }}
                  />
                ))
              )}
            </Box>
          </Stack>
        </ListItem>
        <Divider />
        <ListItem disableGutters>
          <Stack spacing={1} sx={{ mt: 1, width: "100%" }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}>{workStatusLabel}</Typography>
            <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "text.primary" }}>
              {data?.talent?.work_status == UserWorkStatus.SOLE_PROPRIETOR
                ? soleProprietorLabel
                : data?.talent?.work_status == UserWorkStatus.ENTREPRENEUR
                  ? entrepreneurLabel
                  : data?.talent?.work_status == UserWorkStatus.PART_TIME
                    ? partTimeLabel
                    : notSetLabel}
            </Typography>
          </Stack>
        </ListItem>
        <Divider />
        <ListItem disableGutters>
          <Stack spacing={1} sx={{ mt: 1, width: "100%" }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}>{availabilityLabel}</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {data?.talent?.avail_monday && (
                <Chip
                  label="Mon"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(18, 144, 60, 0.1)",
                    color: "#12903C",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: 1.5,
                    border: "1px solid rgba(18, 144, 60, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(18, 144, 60, 0.15)",
                      border: "1px solid rgba(18, 144, 60, 0.3)",
                    }
                  }}
                />
              )}
              {data?.talent?.avail_tuesday && (
                <Chip
                  label="Tue"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(18, 144, 60, 0.1)",
                    color: "#12903C",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: 1.5,
                    border: "1px solid rgba(18, 144, 60, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(18, 144, 60, 0.15)",
                      border: "1px solid rgba(18, 144, 60, 0.3)",
                    }
                  }}
                />
              )}
              {data?.talent?.avail_wednesday && (
                <Chip
                  label="Wed"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(18, 144, 60, 0.1)",
                    color: "#12903C",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: 1.5,
                    border: "1px solid rgba(18, 144, 60, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(18, 144, 60, 0.15)",
                      border: "1px solid rgba(18, 144, 60, 0.3)",
                    }
                  }}
                />
              )}
              {data?.talent?.avail_thursday && (
                <Chip
                  label="Thu"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(18, 144, 60, 0.1)",
                    color: "#12903C",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: 1.5,
                    border: "1px solid rgba(18, 144, 60, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(18, 144, 60, 0.15)",
                      border: "1px solid rgba(18, 144, 60, 0.3)",
                    }
                  }}
                />
              )}
              {data?.talent?.avail_friday && (
                <Chip
                  label="Fri"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(18, 144, 60, 0.1)",
                    color: "#12903C",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: 1.5,
                    border: "1px solid rgba(18, 144, 60, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(18, 144, 60, 0.15)",
                      border: "1px solid rgba(18, 144, 60, 0.3)",
                    }
                  }}
                />
              )}
              {data?.talent?.avail_saturday && (
                <Chip
                  label="Sat"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(18, 144, 60, 0.1)",
                    color: "#12903C",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: 1.5,
                    border: "1px solid rgba(18, 144, 60, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(18, 144, 60, 0.15)",
                      border: "1px solid rgba(18, 144, 60, 0.3)",
                    }
                  }}
                />
              )}
              {data?.talent?.avail_sunday && (
                <Chip
                  label="Sun"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(18, 144, 60, 0.1)",
                    color: "#12903C",
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    height: 24,
                    borderRadius: 1.5,
                    border: "1px solid rgba(18, 144, 60, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(18, 144, 60, 0.15)",
                      border: "1px solid rgba(18, 144, 60, 0.3)",
                    }
                  }}
                />
              )}
            </Stack>
          </Stack>
        </ListItem>
        <Divider />
        <ListItem disableGutters>
          <Stack spacing={1} sx={{ mt: 1, width: "100%" }}>
            <Typography sx={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}>{moreInfoLabel}</Typography>
            <Box>
              <ListItemText
                primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
                primary={rateLabel}
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {`${data?.min_pay} - ${data?.max_pay} $/hr`}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
              <ListItemText
                primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
                primary={commuteRadiusLabel}
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <StraightenIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {`${data?.commute_radius} miles`}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
              <ListItemText
                primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
                primary={allowBackgroundCheckLabel}
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <VerifiedUserIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {data?.allow_background_check ? yesLabel : noLabel}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
              <ListItemText
                primaryTypographyProps={{ fontSize: { xs: 12, sm: 14 }, color: "#12903C", fontWeight: 600 }}
                primary={allowDrugTestLabel}
                secondary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                    <ScienceIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      {data?.allow_drug_test ? yesLabel : noLabel}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Stack>
        </ListItem>
        <Divider />
      </List>
      <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate(`/independent-professionals/${data?.id}`)}
          disabled={company?.status != CompanyStatus.ACTIVE}
        >
          {moreDetailLabel}
        </Button>
      </Box>
    </>
  );

  return (
    <GlassDrawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420, md: 460 },
        },
      }}
      contentProps={{ role: "presentation" }}
    >
      {list()}
    </GlassDrawer>
  );
}
